import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Globe, Wrench, Cloud, ShieldCheck, Palette, MessageSquare, ExternalLink, Accessibility } from 'lucide-react';

const WebsitesPage = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Palette, title: 'Custom Design', desc: 'Unique designs tailored to your brand — no cookie-cutter templates.' },
    { icon: Cloud, title: 'Hosting Included', desc: 'Fast, reliable hosting with 99.9% uptime. We handle everything.' },
    { icon: Wrench, title: 'Ongoing Maintenance', desc: 'Security updates, content changes, backups — all taken care of.' },
    { icon: ShieldCheck, title: 'SSL & Security', desc: 'Every site comes with SSL certificates and security monitoring.' },
    { icon: Globe, title: 'Easy to Manage', desc: 'No more clunky admin panels. Simple, intuitive content management.' },
    { icon: MessageSquare, title: 'Live Chat Ready', desc: 'Built-in live chat so your visitors can reach you instantly.' },
    { icon: Accessibility, title: 'WCAG 2.1 AA Accessible', desc: 'ADA-compliant designs available. Proper contrast, screen reader support, keyboard navigation, and alt text — so everyone can use your site.' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-5 py-2 mb-8">
            <Globe className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">Custom Web Development</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Websites That Work
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">
              As Hard As You Do
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
            No more clunky admin panels or overpriced agencies. We build clean, fast, easy-to-manage websites — and handle the hosting and maintenance so you don&apos;t have to.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-semibold shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/50 group"
            >
              Get a Free Quote <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Everything You Need. Nothing You Don&apos;t.</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              From design to hosting to maintenance — we handle the entire lifecycle of your website.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <Card key={i} className="group bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">{feature.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase - Lake Eunice Township */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Work</h2>
            <p className="text-slate-400 text-lg">Real websites we&apos;ve built for real clients.</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Screenshot */}
              <div className="relative overflow-hidden bg-slate-950 flex items-center justify-center p-6">
                <div className="rounded-xl overflow-hidden border border-slate-700 shadow-2xl">
                  <img
                    src="/lake_eunice_preview.png"
                    alt="Lake Eunice Township Website"
                    className="w-full h-auto"
                  />
                </div>
                <a
                  href="https://lakeeunicetownship.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-8 right-8 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-lg"
                >
                  Visit Site <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              {/* Details */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4 w-fit">
                  <span className="text-emerald-400 text-xs font-medium">Featured Project</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Lake Eunice Township</h3>
                <p className="text-slate-400 leading-relaxed mb-6">
                  A clean, modern municipal website for Lake Eunice Township in Becker County, Minnesota. Features meeting minutes management, public notices, event calendar, township board directory, and live chat — all easy to manage without any clunky admin panels.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Live Chat', 'Calendar', 'Meeting Minutes', 'Mobile Friendly', 'SSL Secured', 'Easy CMS'].map(tag => (
                    <span key={tag} className="text-xs bg-slate-800 text-slate-300 px-3 py-1 rounded-full border border-slate-700">{tag}</span>
                  ))}
                </div>
                <a
                  href="https://lakeeunicetownship.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:text-emerald-300 font-medium text-sm flex items-center gap-1 transition-colors"
                >
                  lakeeunicetownship.org <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready for a Website That Actually Works?</h2>
          <p className="text-slate-400 text-lg mb-8">
            Stop paying for hosting you can&apos;t figure out. Let us build, host, and maintain your website so you can focus on your business.
          </p>
          <Button
            onClick={() => navigate('/contact')}
            size="lg"
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-semibold shadow-xl shadow-emerald-500/30 group"
          >
            Let&apos;s Talk <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebsitesPage;
